/**
 * MIPS Score Calculator Service
 * Handles automatic calculation of MIPS scores with debouncing
 */

import { MipsDataService } from '@/lib/mips-data-service';
import { toCategoryPayload } from '@/lib/scenario';
import { queryClient } from '@/lib/query-client';

class MIPSScoreCalculatorService {
  private debounceTimer: NodeJS.Timeout | null = null;
  private calculationInProgress = false;
  
  /**
   * Calculate scores with debouncing to avoid excessive API calls
   * @param practiceId - The practice ID
   * @param measures - Array of measures to calculate
   * @param delay - Debounce delay in milliseconds
   * @param year - Program year for calculations (defaults to 2025)
   */
  async calculateWithDebounce(
    practiceId: string,
    measures: any[],
    delay = 500,
    year = 2025
  ): Promise<any> {
    // Clear existing timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    
    // Set new timer
    return new Promise((resolve, reject) => {
      this.debounceTimer = setTimeout(async () => {
        try {
          const result = await this.calculate(practiceId, measures, year);
          resolve(result);
        } catch (error) {
          reject(error);
        }
      }, delay);
    });
  }
  
  /**
   * Immediately calculate scores without debouncing
   * @param practiceId - The practice ID
   * @param measures - Array of measures to calculate
   * @param year - Program year for calculations (defaults to 2025)
   */
  async calculate(practiceId: string, measures: any[], year = 2025): Promise<any> {
    if (this.calculationInProgress) {
      console.log('Calculation already in progress, skipping...');
      return null;
    }
    
    this.calculationInProgress = true;
    
    try {
      const dataService = MipsDataService.getInstance();
      
      // Get practice settings to determine practice size
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      const settingsResponse = await fetch(
        `${apiUrl}/api/practices/${practiceId}/settings?year=${year}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          },
        }
      );
      
      let practiceSize: 'small' | 'large' = 'large';
      if (settingsResponse.ok) {
        const settings = await settingsResponse.json();
        practiceSize = settings.is_small_practice ? 'small' : 'large';
      }
      
      // Per-measure points with the CMS benchmark scorer (shared with the scenario composer)
      const scoredMeasures = measures.map(measure => toCategoryPayload(measure, practiceSize, dataService));
      
      // Send pre-calculated scores to backend
      const response = await fetch(
        `${apiUrl}/api/practices/${practiceId}/mips/scores/calculate?year=${year}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          },
          body: JSON.stringify({ 
            qualityMeasures: scoredMeasures,
            practiceSize,
            forceRecalculate: true,
            usePreCalculatedScores: true // Flag to tell backend to use our calculated scores
          })
        }
      );
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to calculate scores');
      }
      
      const result = await response.json();
      console.log('Scores calculated successfully:', result);
      // The POST just rewrote practice_category_scores — tell the shared
      // stored-scores cache so Insights/Overview stop serving the pre-recalc
      // row (data-freshness review, 2026-08-12). Targeted on purpose: the
      // full invalidatePracticeScoring would churn IA/cost caches on every
      // debounced quality recalc.
      void queryClient.invalidateQueries({ queryKey: ['mips', 'scores', String(practiceId)] });
      return result;
      
    } catch (error) {
      console.error('Error calculating MIPS scores:', error);
      throw error;
    } finally {
      this.calculationInProgress = false;
    }
  }
  
  /**
   * Cancel any pending calculation
   */
  cancelPendingCalculation() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
  }
  
  /**
   * Check if calculation is in progress
   */
  isCalculating(): boolean {
    return this.calculationInProgress;
  }
}

// Export singleton instance
export const mipsScoreCalculator = new MIPSScoreCalculatorService();
